<template>
	<BaseLayout
		:content-full-height="true"
		:footer-full="true"
		:include-padding="false"
		:transparent-header="true"
		:transparent-header-dark-text="true"
	>
		<AssetPageHero
			:hero="assetHeroConfig"
			:form-config="leadCaptureFormConfig"
			:offering-id="flagshipFundOfferingId"
			class="pt-300"
		>
			<template #hero-image>
				<div class="col-6-md mt-225-smo">
					<picture>
						<source :srcset="`${heroImageBasePath}.webp`" type="image/webp" />
						<img :src="`${heroImageBasePath}.png`" fetchpriority="high" alt="Real Estate hero graphic" />
					</picture>
				</div>
			</template>
		</AssetPageHero>
		<FlagshipFundWhyPrivateRealEstateSection theme="light" />
		<BasePlaceHolder
			:line-count="10"
			:section-count="2"
			:placehold="isLoading"
			:class="{ 'container marketing-section': isLoading }"
		>
			<FeaturedFundSection :data="featuredFundSectionData" />
			<RealEstateOurStrategiesSection
				v-if="portfolioStats && strategiesShowcaseSection"
				:portfolio-stats="portfolioStats"
				:strategies-section="strategiesShowcaseSection"
			/>
			<section class="marketing-section bg-color-white">
				<div class="container">
					<div class="grid">
						<div class="col-8-md col-offset-2-md text-align-center mb-200 mb-300-md">
							<h2
								v-if="pageData?.assetsHeading"
								class="heading-2 heading-4-smo mb-100 font-weight-lighter"
							>
								{{ pageData.assetsHeading }}
							</h2>
							<p
								v-if="pageData?.assetsSummary"
								class="body-lg text-color-content-secondary"
								v-html="pageData.assetsSummary"
							></p>
						</div>
					</div>
					<AssetElementContainer
						:asset-elements="assetElements"
						:asset-elements-per-page="12"
						:use-icon-view-options="true"
						:use-grid="true"
					/>
				</div>
			</section>
			<RelatedArticlesSection
				v-if="pageData?.relatedArticlesSection.relatedArticles.length"
				:heading="pageData.relatedArticlesSection.sectionHeading"
				:related-articles="pageData.relatedArticlesSection.relatedArticles"
			/>
			<AddToYourPortfolioSection
				component-id="real-estate"
				:title="ctaPanelHeading"
				:lead-capture-form-config="leadCaptureFormConfig"
				:offering-id="flagshipFundOfferingId"
			/>
		</BasePlaceHolder>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { PortfolioStats, SanityMarketingStatsList, SanityStrategiesShowcase } from 'types/sanity/marketing-components';
import { StrategiesSection, StrategyShowcaseInfo } from 'types/real-estate-strategy';
import AddToYourPortfolioSection from '@components/marketing-site/add-to-your-portfolio-section.vue';
import { appCloudfrontPath } from '@constants';
import { Asset } from 'types/assets';
import AssetElementContainer from '@components/asset-element/asset-element-container.vue';
import AssetPageHero from '@components/marketing-site/asset-page-hero.vue';
import { buildFundKeyStats } from '@utils/offerings';
import FeaturedFundSection from '@components/marketing-site/featured-fund-section.vue';
import { FeaturedFundSectionData } from 'types/marketing-site';
import { flagshipFundOfferingId } from '@constants/reit';
import FlagshipFundWhyPrivateRealEstateSection from '@components/marketing-site/flagship-fund/flagship-fund-why-private-real-estate-section.vue';
import { getAssets } from '@api/sanity/proxy/assets';
import { getFund } from '@api/sanity/proxy/funds';
import { getPortfolioStats } from '@api/sanity/graphql/global-social-stats';
import { getRealEstateMarketingPage } from '@api/sanity/graphql/real-estate-marketing-page';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import { MarketingImageHero } from 'types/layout';
import { RealEstateMarketingLandingPage } from 'types/sanity/real-estate-marketing';
import RealEstateOurStrategiesSection from '@components/marketing-site/real-estate-our-strategies-section.vue';
import { realEstatePresetPlanId } from '@constants/plans-info';
import RelatedArticlesSection from '@components/marketing-site/related-articles-section.vue';
import { SanityImage } from 'types/sanity';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';

const appStore = useAppStore();

const assetHeroConfig: MarketingImageHero = {
	backgroundColor: 'bg-color-greige',
	description:
		'Explore the Fundrise real estate strategies designed to harness long-term macroeconomic drivers like Sunbelt housing and e-commerce logistics.',
	heading: 'Real Estate',
	title: 'An expansive portfolio, calibrated for consistent growth'
};

const heroImageBasePath = `${appCloudfrontPath}/images/marketing-site/hero-images/hero-img-real-estate`;

const featuredFundSectionData = ref<FeaturedFundSectionData>({
	title: 'Flagship Fund',
	description: '',
	fundStats: [],
	ctaLink: {
		text: 'Learn more about the Flagship Real Estate Fund',
		router: 'view-offering-details',
		params: { offeringId: flagshipFundOfferingId }
	},
	assetTypes: [
		{ icon: 'flagship-buildforrent', label: 'Build for rent' },
		{ icon: 'flagship-multifamilyapartments', label: 'Multifamily apartments' },
		{ icon: 'flagship-industrialproperties', label: 'Industrial properties' }
	]
});

const isLoading = ref(true);
const isRePlanFeatureEnabled = ref(false);

const pageData = ref<RealEstateMarketingLandingPage | null>(null);
const assetElements = ref<Array<Asset>>([]);
const portfolioStats = ref<PortfolioStats | null>(null);

const leadCaptureFormConfig = computed((): LeadCaptureFormConfig => {
	const baseConfig: Partial<LeadCaptureFormConfig> = {
		buttonText: 'Sign up'
	};

	if (isRePlanFeatureEnabled.value) {
		return {
			...baseConfig,
			signupFlowType: 'PRESET_PLAN',
			presetPlanId: realEstatePresetPlanId
		};
	} else {
		return {
			...baseConfig,
			signupFlowType: 'STANDARD'
		};
	}
});

const strategiesShowcaseSection = computed((): StrategiesSection => {
	const strategies: Array<StrategyShowcaseInfo> = [];
	const sectionDisclaimer: Array<string> = [pageData.value?.showcaseSectionDisclaimer ?? ''];

	const sanityShowcase = pageData.value?.realEstateStrategiesShowcase;
	if (sanityShowcase) {
		sanityShowcase.forEach((showcase: SanityStrategiesShowcase) => {
			const featuredImages =
				showcase.strategyImages.map((img: SanityImage) => {
					return img.asset?.url ?? '';
				}) ?? [];

			const keyValStats =
				showcase.strategyStats?.map((stat: SanityMarketingStatsList) => ({
					key: stat.value ?? '',
					value: stat.label ?? ''
				})) ?? [];

			const strategyInfo: StrategyShowcaseInfo = {
				eyebrow: '',
				title: showcase.heading,
				summary: showcase.summary,
				link: {
					href: showcase.showcaseLink?.url ?? '',
					text: showcase.showcaseLink?.linkText ?? '',
					target: showcase.showcaseLink?.openInNewTab ? '_blank' : '_self'
				},
				stats: keyValStats,
				featuredImages
			};

			strategies.push(strategyInfo);
		});
	}

	return {
		strategies,
		sectionDisclaimer
	};
});

const ctaPanelHeading = computed(() => {
	return pageData.value?.ctaPanelHeading ?? '';
});

async function setup(): Promise<void> {
	try {
		isRePlanFeatureEnabled.value = await appStore.isFeatureEnabled('real-estate-plan-available');

		const [realEstatePageData, assetsData, portfolioStatsData, fundsData] = await Promise.all([
			getRealEstateMarketingPage(),
			getAssets({ assetClass: 'REAL_ESTATE' }),
			getPortfolioStats(),
			getFund(flagshipFundOfferingId, { include: ['ANNUALIZED_RETURNS'] })
		]);

		pageData.value = realEstatePageData;
		assetElements.value = assetsData.results;
		portfolioStats.value = portfolioStatsData;

		if (fundsData) {
			featuredFundSectionData.value.fundStats = buildFundKeyStats(fundsData);
			featuredFundSectionData.value.description = fundsData.fundSummary ?? '';
		}

		useHead({
			title: 'Real Estate Strategies',
			meta: [
				{ property: 'og:title', content: 'Real Estate Strategies' },
				{ name: 'description', content: assetHeroConfig.description },
				{ property: 'og:description', content: assetHeroConfig.description }
			]
		});

		isLoading.value = false;
	} catch {}
}

setup();
</script>
